import { makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMoreOutlined'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import classNames from 'classnames'
import { BetaChip } from 'elements/chip/BetaChip'
import { Accordion, AccordionDetails, AccordionSummary, Box, styled, Tooltip } from 'opensolar-ui'
import { BooleanInput } from 'ra-ui-materialui'
import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { useForm, useFormState } from 'react-final-form'

interface StyleProps {
  disableGutters?: boolean
}

const useStyles = makeStyles(() => ({
  root: (props: StyleProps) => ({
    margin: '0 !important',
    '&.Mui-expanded': { margin: '0 !important' },
    '&::before': { display: 'none' },
    ...(props.disableGutters ? { margin: '0 !important' } : {}),
  }),
  summaryRoot: (props: StyleProps) =>
    props.disableGutters
      ? {
          padding: '0px',
        }
      : {},
  summaryIcon: (props: StyleProps) =>
    props.disableGutters
      ? {
          marginRight: '0px',
        }
      : {},
  content: (props: StyleProps) => ({
    alignItems: 'center',
    width: '100%',
    ...(props.disableGutters && {
      margin: '0px !important',
      padding: '0px',
    }),
  }),
  headingTitle: {
    fontWeight: 500,
  },
  disabled: {
    pointerEvents: 'none',
    cursor: 'not-allowed',
  },
  hidden: {
    position: 'relative',
  },
  detail: {},
}))

type BasePropTypes = {
  className?: string
  defaultExpanded?: boolean
  detailsClassName?: string
  classes?: { summaryRoot?: string }
  disableGutters?: boolean
  disabled?: boolean
  expanded?: boolean
  elevation?: number
  lazyRender?: boolean
  name: string
  titleTooltip?: React.ReactNode
  onChange?: (expanded: boolean) => void
  summaryContent?: ReactNode
  isBeta?: boolean
  title?: string | ReactNode
  titleIcon?: React.ComponentType
  variant?: string
  id?: string
  removeCollapseBorder?: boolean
  preventPacking?: boolean
  ux3?: boolean
  children?: React.ReactNode
}

type ContentPropTypes<T> = BasePropTypes & {
  content: React.FunctionComponent<T>
  contentProps?: T
}

type RenderPropTypes = BasePropTypes & {
  render: () => ReactNode // won't be reactive, as most often these are defined inline
}

type PropTypes<T> = ContentPropTypes<T> | RenderPropTypes | BasePropTypes

const RestyledAccordion = styled(Accordion, { name: 'RestyledAccordion' })<{
  removeCollapseBorder?: boolean
  preventPacking?: boolean
}>(({ removeCollapseBorder, preventPacking, theme }) => ({
  width: '100%',
  borderRadius: 8,
  margin: '0 !important',
  '&.Mui-expanded': { margin: '0 !important' },
  '&::before': { display: 'none' },
  '& .MuiCollapse-container': {
    borderTop: removeCollapseBorder ? 'none' : theme.mixins.borderStandard.border,
    margin: 0,
  },
  '& .MuiAccordionSummary-content': {
    '& span': {
      fontWeight: 500,
    },
  },
  ...(removeCollapseBorder && {
    border: 'none !important',
  }),
}))

export const AccordionCard: React.FunctionComponent<PropTypes<any>> = (props) => {
  const classes = useStyles({ disableGutters: !!props.disableGutters })

  //@ts-ignore
  const titleEl = useMemo(() => (props.titleIcon ? React.createElement(props.titleIcon) : null), [props.titleIcon])
  const contentEl = useMemo(() => {
    if (props['render']) return (props as RenderPropTypes).render()
    else if (props['content']) {
      const { content, contentProps, ...rest } = props as ContentPropTypes<any>
      return React.createElement(content, { ...rest, ...(contentProps || {}) })
    } else return props.children
  }, [props['content'], props['contentProps'], props])

  const [isExpanded, setIsExpanded] = useState<boolean>(!!props.defaultExpanded)
  const [hasExpanded, setHasExpanded] = useState<boolean>(isExpanded)

  useEffect(() => {
    if (props.expanded !== undefined) setIsExpanded(props.expanded)
  }, [props.expanded])

  useEffect(() => {
    if (isExpanded) setHasExpanded(isExpanded)
  }, [isExpanded])

  const onChange = () => {
    const newValue = !isExpanded
    setIsExpanded(newValue)
    if (props.onChange) props.onChange(newValue)
  }

  return (
    <RestyledAccordion
      classes={{ root: classNames(classes.root, props.className) }}
      defaultExpanded={props.defaultExpanded}
      expanded={isExpanded}
      //@ts-ignore
      TransitionProps={{ classes: { hidden: classes.hidden } }}
      key={props.name}
      elevation={props.elevation}
      onChange={onChange}
      id={props.id}
      removeCollapseBorder={props.removeCollapseBorder}
      preventPacking={!!props.preventPacking}
    >
      <AccordionSummary
        aria-controls={`expansion-content-${props.name}`}
        classes={{
          content: classes.content,
          root: classNames(classes.summaryRoot, props.classes?.summaryRoot),
          //@ts-ignore
          expandIcon: classes.summaryIcon,
        }}
        expandIcon={<ExpandMoreIcon />}
        id={`expansion-header-${props.name}`}
      >
        <Box justifyContent={'space-between'} display="flex" alignItems={'center'} flexGrow={1}>
          <Box display="flex" alignItems={'center'}>
            {titleEl && <span style={{ marginRight: '10px' }}>{titleEl}</span>}
            {props.title && (
              <Box justifyContent={'center'} display="flex" gridGap={5} className={classes.headingTitle}>
                {props.title}
                {props.titleTooltip && (
                  <Tooltip title={props.titleTooltip}>
                    <InfoOutlinedIcon color="action" fontSize="small" />
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
          {props.isBeta && <BetaChip />}
        </Box>
        {props.summaryContent}
      </AccordionSummary>
      <AccordionDetails
        className={classNames([props.disabled ? classes.disabled : classes.detail, props.detailsClassName])}
      >
        {!hasExpanded && props.lazyRender ? null : contentEl}
      </AccordionDetails>
    </RestyledAccordion>
  )
}

type StoredPropTypes = Omit<PropTypes<any>, 'expanded' | 'title'> & {
  title: string
  resource: string
  expandedSource: string
}

// If the open state should be stored in a form field, use this
export const StoredAccordionCard: React.FunctionComponent<StoredPropTypes> = ({
  title,
  resource,
  expandedSource,
  defaultExpanded,
  onChange,
  ...props
}) => {
  const form = useForm()
  const formState = useFormState()
  const titleWrapped = (
    <BooleanInput
      key={expandedSource}
      label={title}
      source={expandedSource}
      name={expandedSource}
      defaultValue={defaultExpanded}
      disabled={props.disabled}
      labelPlacement="end"
    />
  )

  return (
    <AccordionCard
      {...props}
      title={titleWrapped}
      expanded={formState.values[expandedSource]}
      onChange={(value) => form.change(expandedSource, value)}
    />
  )
}

export default AccordionCard
